import type { AtsJobSelection } from '@/features/ats-job-picker/ats-job-picker'
import { campaignChannel, type SubmitCampaignForReviewData } from '@jobilla/nexus-sdk'
import { isAxiosError } from 'axios'
import { createStore, useAtomValue } from 'jotai'
import { Ref, useCallback, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OutPortal } from 'react-reverse-portal'
import { v4 } from 'uuid'
import { useBrandLibraryStore } from '../constructs/branding/simplified'
import { brandLibraryLoadingAtom } from '../constructs/branding/simplified/state'
import { useAlert } from '../rapid-ui'
import { logger } from '../utilities'
import {
	EditModeDetailsAndBranding,
	NewModeDetailsAndBranding,
	NewModeDetailsAndBrandingProps,
} from './details-and-branding'
import { EditingEditArea, GeneratingEditArea, PendingCreateEditArea } from './edit-panel'
import { EditorEditSimulators, EditorEditSimulatorsRef } from './editor-edit-simulators'
import { useEditorContext } from './editor-provider'
import { onRedirectAfterCompleteAtom, onReviewCompleteAtom } from './hooks'
import { NewModeSimulators } from './new-mode-simulators'
import { ReviewForm } from './review'
import { EditorShell } from './shell'
import { campaignDetailsStatusAtom, campaignReviewAtom } from './state'

export type NewModeEditorProps = {
	onGenerateFromScan: (uuid: string, url: string, brandId: string, generateKeyImage: boolean) => Promise<void>
	onGenerateFromAtsJob: (
		uuid: string,
		atsJob: AtsJobSelection,
		brandId: string,
		generateKeyImage: boolean,
	) => Promise<void>
	onGenerateFromManual: (
		uuid: string,
		data: {
			title: string
			location: string
			language: string
			requirements: string
		},
		brandId: string,
		generateKeyImage: boolean,
	) => Promise<void>
	brandStore?: ReturnType<typeof createStore>
	organisationId: string
	initialValues?: {
		title?: string
		location?: string
		language?: string
		requirements?: string
		url?: string
		mode?: 'scan' | 'manual' | 'ats'
		scrapeError?: string
	}
}

export type EditModeEditorProps = {
	isGenerated: boolean // we could derive this from the status atoms instead, so consumers don't need to think about it
	campaignId: string
	serviceFeePercent: number
	brandStore?: ReturnType<typeof createStore>
	thankYouStore?: ReturnType<typeof createStore>
	ref: Ref<{
		renderFeedAd: () => Promise<Blob>
		renderStoryAd: () => Promise<Blob>
	}>
}

function firstValidationErrorPerField(error: unknown): Record<string, string> | undefined {
	if (!isAxiosError<{ errors?: Record<string, string[]> }>(error) || error.response?.status !== 422) {
		return undefined
	}

	const errors = error.response.data.errors ?? {}

	return Object.fromEntries(
		Object.entries(errors)
			.filter(([, messages]) => messages.length > 0)
			.map(([field, messages]) => [field, messages[0]]),
	)
}

export function NewModeEditor(props: NewModeEditorProps) {
	const { t } = useTranslation('common')
	const alert = useAlert()
	const { editorPortalNode, shellRef } = useEditorContext()
	const [isCreating, setIsCreating] = useState(false)
	const [serverErrors, setServerErrors] = useState<Record<string, string>>()

	// determining brand library loading state
	const store = useBrandLibraryStore({ overrideStore: props.brandStore })
	const brandLibraryLoading = useAtomValue(brandLibraryLoadingAtom, { store })

	if (!shellRef || !editorPortalNode) {
		throw new Error('shellRef or editorPortalNode is not set')
	}

	const handleGenerateFailure = useCallback((error: unknown, action: string, fallbackMessage: string) => {
		const validationErrors = firstValidationErrorPerField(error)

		if (!validationErrors) {
			logger.error(error, { tags: { component: 'Editor', action } })
			alert.open(t('common.error'), fallbackMessage, t('common.ok'), () => {
				setIsCreating(false)
				// TODO: do we need to do any form resets?
			})
			return
		}

		logger.warn('Generate request failed validation', {
			category: 'campaign_creation',
			data: { action, fields: Object.keys(validationErrors) },
		})
		setIsCreating(false)
		shellRef.current?.moveToDetailsStep()
		setServerErrors(validationErrors)
	}, [setIsCreating, shellRef, t, alert])

	const handleGenerateFromScan = useCallback<NewModeDetailsAndBrandingProps['onGenerateFromScan']>((...args) => {
		setIsCreating(true)
		shellRef.current?.moveToEditStep()
		props.onGenerateFromScan(v4(), ...args).catch((error) => {
			handleGenerateFailure(error, 'generate-from-scan', t('editor.errors.failedScan'))
		})
		// no .then; we expect consumers to redirect
	}, [props.onGenerateFromScan, setIsCreating, shellRef, t, handleGenerateFailure])

	const handleGenerateFromAtsJob = useCallback<NewModeDetailsAndBrandingProps['onGenerateFromAtsJob']>((...args) => {
		setIsCreating(true)
		shellRef.current?.moveToEditStep()
		props.onGenerateFromAtsJob(v4(), ...args).catch((error) => {
			handleGenerateFailure(error, 'generate-from-ats-job', t('editor.errors.failedGenerate'))
		})
		// no .then; we expect consumers to redirect
	}, [props.onGenerateFromAtsJob, setIsCreating, shellRef, t, handleGenerateFailure])

	const handleGenerateFromManual = useCallback<NewModeDetailsAndBrandingProps['onGenerateFromManual']>((...args) => {
		setIsCreating(true)
		shellRef.current?.moveToEditStep()
		props.onGenerateFromManual(v4(), ...args).catch((error) => {
			handleGenerateFailure(error, 'generate-from-manual', t('editor.errors.failedGenerate'))
		})
		// no .then; we expect consumers to redirect
	}, [props.onGenerateFromManual, setIsCreating, shellRef, t, handleGenerateFailure])

	// todo: lock this while loading; bug appears to come up with validation of campaign details if you submit before it's valid
	return (
		<OutPortal<typeof EditorShell>
			node={editorPortalNode}
			isLoading={brandLibraryLoading}
			enabledPanelIds={isCreating ? ['details', 'edit'] : ['details']}
			defaultPanelId={'details'}
			detailsPanel={
				<div>
					<NewModeDetailsAndBranding
						onGenerateFromScan={handleGenerateFromScan}
						onGenerateFromAtsJob={handleGenerateFromAtsJob}
						onGenerateFromManual={handleGenerateFromManual}
						brandStore={props.brandStore}
						organisationId={props.organisationId}
						initialValues={props.initialValues}
						serverErrors={serverErrors}
					/>
				</div>
			}
			editPanel={
				<div>
					<PendingCreateEditArea />
				</div>
			}
			reviewPanel={<div>{t('editor.review')}</div>}
			simulators={<NewModeSimulators />}
		/>
	)
}

export function EditModeEditor(props: EditModeEditorProps) {
	const { editorPortalNode, shellRef } = useEditorContext()

	const simulatorsRef = useRef<EditorEditSimulatorsRef>({
		renderFeedAd: () => Promise.reject(new Error('No feed ad tools ref')),
		renderStoryAd: () => Promise.reject(new Error('No story ad tools ref')),
	})

	useImperativeHandle(props.ref, () => simulatorsRef.current, [simulatorsRef])

	if (!shellRef || !editorPortalNode) {
		throw new Error('shellRef or editorPortalNode is not set')
	}

	// determining brand library loading state
	const store = useBrandLibraryStore({ overrideStore: props.brandStore })
	const brandLibraryLoading = useAtomValue(brandLibraryLoadingAtom, { store })

	const campaignStatus = useAtomValue(campaignDetailsStatusAtom)

	// Get completion callbacks from atoms
	const onReviewComplete = useAtomValue(onReviewCompleteAtom)
	const onRedirectAfterComplete = useAtomValue(onRedirectAfterCompleteAtom)
	const review = useAtomValue(campaignReviewAtom)

	const renderPreviews = useCallback(async () => {
		const [feedAdBlob, storyAdBlob] = await Promise.all([
			simulatorsRef.current.renderFeedAd(),
			simulatorsRef.current.renderStoryAd(),
		])
		return { feedAdBlob, storyAdBlob }
	}, [])

	const handleComplete = useCallback(
		async (
			paymentMethods: SubmitCampaignForReviewData['paymentMethods'],
			stripePaymentMethodId?: string,
			profileId?: string | null,
			inventoryItems?: SubmitCampaignForReviewData['inventoryItems'],
		) => {
			if (!review) {
				throw new Error('Campaign review data is not available')
			}

			const hasMetaChannel = review.channels.includes(campaignChannel.META)
			const previews = hasMetaChannel ? await renderPreviews() : undefined

			await onReviewComplete?.({
				feedAd: previews?.feedAdBlob,
				storyAd: previews?.storyAdBlob,
				budget: review.budget,
				startDate: review.startDate,
				endDate: review.endDate,
				custom_fields: review.custom_fields,
				predictedBudget: review.predictedBudget,
				channels: review.channels,
				paymentMethods,
				stripePaymentMethodId,
				profileId,
				inventoryItems,
			})
		},
		[onReviewComplete, review, renderPreviews],
	)

	return (
		<OutPortal<typeof EditorShell>
			node={editorPortalNode}
			campaignId={props.campaignId}
			isLoading={brandLibraryLoading || ['pending', 'idle'].includes(campaignStatus)}
			enabledPanelIds={props.isGenerated ? ['details', 'edit', 'review'] : ['details', 'edit']}
			defaultPanelId={'edit'}
			detailsPanel={
				<div>
					<EditModeDetailsAndBranding
						brandStore={props.brandStore}
						campaignId={props.campaignId}
					/>
				</div>
			}
			editPanel={
				<div className="h-full">
					{props.isGenerated
						? <EditingEditArea campaignId={props.campaignId} />
						: <GeneratingEditArea />}
				</div>
			}
			reviewPanel={
				<div>
					{onReviewComplete && onRedirectAfterComplete && (
						<ReviewForm
							campaignId={props.campaignId}
							serviceFeePercent={props.serviceFeePercent}
							onReviewComplete={handleComplete}
							onRedirectAfterComplete={onRedirectAfterComplete}
							renderPreviews={renderPreviews}
						/>
					)}
				</div>
			}
			simulators={
				<EditorEditSimulators
					ref={simulatorsRef}
					thankYouStore={props.thankYouStore}
					campaignId={props.campaignId}
				/>
			}
		/>
	)
}
